import React from 'react'
import { Helmet } from 'react-helmet';
import AppLayoutTitle from './appLayoutTitle';
import Swal from 'sweetalert2';
import { apiGet, apiPost } from '../../helper/helperApi';
import { handleException } from '../../helper/helperError';
import ComTable from './../components/comTable';
import { createRoot } from 'react-dom/client';
import ComDeliveryEditor from './../components/comDeliveryEditor';


const AppEmailDelivery = () => {
    const [dataset, setDataset] = React.useState(undefined);
    const [activeDomains, setActiveDomains] = React.useState(undefined);
    const firstTimeUse = (dataset !== undefined && Array.isArray(dataset) && dataset.length === 0);

    const fetchData = async () => {
        try {
            apiGet("/deliveries")
                .then((res) => { setDataset(res?.data || []); })
                .catch((ex) => handleException(ex, "Failed to fetch deliveries data."));
            apiGet("/domains/activedomains")
                .then((res) => { setActiveDomains(res.data || []); })
                .catch((ex) => handleException(ex, "Failed to fetch active domains data!"));
        } catch (ex) {
            handleException(ex, "Failed to fetch deliveries data!");
        }
    }

    React.useEffect(() => {
        fetchData();
    }, []);

    const handleAdd = async () => {
        // 1- Show the form
        let formData = null;
        Swal.fire({
            title: 'Add New Delivery Setting',
            html: '<div id="swal-react-root"></div>',
            width: "640px",
            focusConfirm: false,
            showCancelButton: true,
            confirmButtonText: 'Add Delivery Setting',
            customClass: {
                confirmButton: 'btn-gold text-dark',
                cancelButton: 'btn-light'
            },
            didOpen: () => {
                const swalRoot = document.getElementById('swal-react-root');
                if (swalRoot) {
                    const root = createRoot(swalRoot);
                    root.render(
                        <ComDeliveryEditor
                            activeDomains={activeDomains}
                            onDataChange={(data) => { formData = data; }}
                        />
                    );
                }
            },
            preConfirm: () => {
                if (!formData
                    || !formData.domainFrom
                    || !formData.deliveryMethod
                    || (formData.deliveryMethod === "custom" && !formData.transportServer)
                    || (formData.deliveryMethod === "custom" && !formData.transportPort)
                    || (formData.deliveryMethod === "custom" && !formData.transportUser)
                    || (formData.deliveryMethod === "custom" && !formData.transportPass)
                ) {
                    Swal.showValidationMessage(`Please fill all fields`);
                }
                // Check if the domain is already in use
                if (dataset && dataset.some(item => item.domainFrom === formData.domainFrom)) {
                    Swal.showValidationMessage(`The domain "${formData.domainFrom}" is already having a delivery setting.`);
                }
                return formData;
            },
        }).then((result) => {
            if (result.isConfirmed) {
                const newData = { ...result.value };
                apiPost("/deliveries/new", newData)
                    .then(() => {
                        Swal.fire({
                            title: 'Added!',
                            text: 'Your new delivery setting has been added.',
                            icon: 'success',
                            timer: 1500,
                        });
                        fetchData();
                    })
                    .catch((ex) => {
                        handleException(ex, "Failed to add new delivery setting");
                        fetchData();
                    })
            }
        });
    }

    const handleEdit = async (row) => {
        if (!row || !row.id) return handleException(null, "Invalid delivery data");

        try {
            const res = await apiGet(`/deliveries/${row.id}`);
            const deliveryData = res.data;
            let formData = { ...deliveryData };
            Swal.fire({
                title: 'Edit Delivery Setting',
                html: '<div id="swal-react-root"></div>',
                width: "640px",
                focusConfirm: false,
                showCancelButton: true,
                confirmButtonText: 'Save Changes',
                customClass: {
                    confirmButton: 'btn-gold text-dark',
                    cancelButton: 'btn-light'
                },
                didOpen: () => {
                    const swalRoot = document.getElementById('swal-react-root');
                    if (swalRoot) {
                        const root = createRoot(swalRoot);
                        root.render(
                            <ComDeliveryEditor
                                initialData={deliveryData}
                                activeDomains={activeDomains}
                                onDataChange={(data) => { formData = data; }}
                            />
                        );
                    }
                },
                preConfirm: () => {
                    if (!formData
                        || !formData.domainFrom
                        || !formData.deliveryMethod
                        || (formData.deliveryMethod === "custom" && !formData.transportServer)
                        || (formData.deliveryMethod === "custom" && !formData.transportPort)
                        || (formData.deliveryMethod === "custom" && !formData.transportUser)
                        || (formData.deliveryMethod === "custom" && !formData.transportPass)
                    ) {
                        Swal.showValidationMessage(`Please fill all fields`);
                    }
                    // Check if the domain is already in use
                    if (dataset && dataset.some(item => item.domainFrom === formData.domainFrom && item.id !== row.id)) {
                        Swal.showValidationMessage(`The domain "${formData.domainFrom}" is already having a delivery setting!`);
                    }
                    return formData;
                },
            }).then((result) => {
                if (result.isConfirmed) {
                    apiPost(`/deliveries/${row.id}/update`, {
                        ...result.value,
                    })
                        .then(() => {
                            Swal.fire({
                                title: 'Updated!',
                                text: 'Your delivery setting has been updated.',
                                icon: 'success',
                                timer: 1500,
                            });
                        })
                        .catch((ex) => {
                            handleException(ex, "Failed to update delivery setting");
                        })
                        .finally(() => {
                            fetchData();
                        })
                }
            });
        }
        catch (ex) {
            handleException(ex, "Failed to edit delivery setting");
        }
    }

    const handleDelete = async (row) => {
        if (!row || !row.id) return handleException(null, "Invalid delivery data");
        Swal.fire({
            title: 'Delete Delivery Setting',
            text: `Are you sure you want to delete this delivery setting? This action cannot be undone.`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Delete Delivery Setting',
            customClass: {
                confirmButton: 'btn-gold text-dark',
                cancelButton: 'btn-light'
            },
        }).then((result) => {
            if (result.isConfirmed) {
                apiPost(`/deliveries/${row.id}/delete`)
                    .then(() => {
                        Swal.fire({
                            title: 'Deleted!',
                            text: 'Your delivery setting has been deleted.',
                            icon: 'success',
                            timer: 1500,
                        });
                    })
                    .catch((ex) => {
                        handleException(ex, "Failed to delete delivery setting");
                    })
                    .finally(() => {
                        fetchData();
                    })
            }
        });
    }


    return <>
        <Helmet>
            <title>Email Delivery Settings | Sparkling Email</title>
        </Helmet>
        <>
            <AppLayoutTitle title="Email Delivery Settings" buttons={
                <button className="btn btn-gold" onClick={handleAdd}>Add New Delivery Setting</button>
            } />
            <div className="px-3 py-3">
                <div>
                    Configure how processed emails are delivered back to your organization's
                    mail server or chosen destination after translation and/or signature.
                </div>
                <div>
                    Use this section to specify how processed emails are returned to your organization
                    after translation and/or signature are applied. For each domain, you can define the
                    delivery destination—such as Microsoft 365, a custom SMTP server, or another mail platform.
                    This ensures all outgoing emails are routed exactly where you need them, with maximum
                    flexibility and control for your IT team.
                </div>
                <br />
                {(firstTimeUse === true)
                    ? <div className="text-center my-5">
                        <button className="btn btn-lg btn-outline-secondary" onClick={handleAdd}>
                            Add First Delivery Setting
                        </button>
                    </div>
                    : <ComTable
                        data={dataset}
                        isUndefinedIsLoading={true}
                        handleDelete={(row) => handleDelete(row)}
                        handleEdit={(row) => handleEdit(row)}
                    />
                }
            </div>
        </>
    </>
}

export default AppEmailDelivery;